import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["type", "size", "htmlSize", "display", "textAlign", "placeholder", "value", "defaultValue", "isRequired", "onFocus"];
var _dec, _dec2, _dec3, _class, _TextInput;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Fragment, Component } from 'react';
import { callRenderProp, getInteraction, passthroughProps, withDeterministicId } from '@instructure/ui-react-utils';
import { isActiveElement, addEventListener } from '@instructure/ui-dom-utils';
import { FormField } from '@instructure/ui-form-field';
import { testable } from '@instructure/ui-testable';
import { withStyle } from '@instructure/emotion';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { allowedProps, propTypes } from './props';

/**
---
category: components
tags: form, field
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let TextInput = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec3 = testable(), _dec(_class = _dec2(_class = _dec3(_class = (_TextInput = class TextInput extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._input = null;
    this._afterElement = null;
    this._defaultId = void 0;
    this._messagesId = void 0;
    this._focusListener = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.makeStyleProps = () => {
      const interaction = this.interaction;
      const _this$state = this.state,
        hasFocus = _this$state.hasFocus,
        afterElementHasWidth = _this$state.afterElementHasWidth;
      const beforeElement = this.props.renderBeforeInput ? callRenderProp(this.props.renderBeforeInput) : null;
      return {
        disabled: interaction === 'disabled',
        invalid: this.invalid,
        focused: hasFocus,
        afterElementHasWidth: afterElementHasWidth,
        beforeElementExists: !!beforeElement
      };
    };
    this.handleInputRef = node => {
      this._input = node;
      if (typeof this.props.inputRef === 'function') {
        this.props.inputRef(node);
      }
    };
    this.handleChange = event => {
      if (typeof this.props.onChange === 'function') {
        this.props.onChange(event, event.target.value);
      }
    };
    this.handleBlur = event => {
      if (typeof this.props.onBlur === 'function') {
        this.props.onBlur(event);
      }
      this.setState({
        hasFocus: false
      });
    };
    this.handleFocus = event => {
      if (typeof this.props.onFocus === 'function') {
        this.props.onFocus(event);
      }
      this.setState({
        hasFocus: true
      });
    };
    this.state = {
      hasFocus: false,
      afterElementHasWidth: void 0
    };
    this._defaultId = props.deterministicId();
    this._messagesId = props.deterministicId('TextInput-messages');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    if (this._input) {
      this._focusListener = addEventListener(this._input, 'focus', this.handleFocus);
      this.setState({
        afterElementHasWidth: this.getElementHasWidth(this._afterElement)
      });
    }
    this.adjustAfterElementHeight();
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStyleProps());
  }
  componentWillUnmount() {
    if (this._focusListener) {
      this._focusListener.remove();
    }
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    if (prevProps.renderAfterInput !== this.props.renderAfterInput) {
      this.setState({
        afterElementHasWidth: this.getElementHasWidth(this._afterElement)
      });
    }
    if (getInteraction({
      props: prevProps
    }) !== 'disabled' && getInteraction({
      props: this.props
    }) === 'disabled') {
      this.setState({
        hasFocus: false
      });
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStyleProps());
  }
  adjustAfterElementHeight() {
    var _this$_afterElement, _afterElementChild$fi;
    const afterElementChild = (_this$_afterElement = this._afterElement) === null || _this$_afterElement === void 0 ? void 0 : _this$_afterElement.firstElementChild;

    // Check if the child is a button, then get the button's first child (the content span)
    const buttonContentSpan = (afterElementChild === null || afterElementChild === void 0 ? void 0 : afterElementChild.tagName) === 'BUTTON' ? afterElementChild.firstElementChild : null;

    // This is a necessary workaround for DateInput2 because it uses a Popover, which has a nested Button as an afterElement
    // Check if the child is a Popover's inner span containing a button, then get the button's first child (the content span)
    const popoverContentSpan = (afterElementChild === null || afterElementChild === void 0 ? void 0 : afterElementChild.tagName) === 'SPAN' && ((_afterElementChild$fi = afterElementChild.firstElementChild) === null || _afterElementChild$fi === void 0 ? void 0 : _afterElementChild$fi.tagName) === 'BUTTON' ? afterElementChild.firstElementChild.firstElementChild : null;
    const targetSpan = buttonContentSpan !== null && buttonContentSpan !== void 0 ? buttonContentSpan : popoverContentSpan;
    if (targetSpan) {
      // Set the height to 36px (the height of a medium TextInput) to avoid layout shift when the afterElement content changes
      // this temporary workaround is necessary because otherwise the layout breaks, later on IconButton's default height will be adjusted to the TextInput size
      // so this workaround will not be needed anymore
      targetSpan.style.height = '36px';
    }
  }
  focus() {
    var _this$_input;
    (_this$_input = this._input) === null || _this$_input === void 0 ? void 0 : _this$_input.focus();
  }
  get interaction() {
    return getInteraction({
      props: this.props
    });
  }
  get hasMessages() {
    return !!this.props.messages && this.props.messages.length > 0;
  }
  get invalid() {
    return !!this.props.messages && this.props.messages.findIndex(message => {
      return message.type === 'error' || message.type === 'newError';
    }) >= 0;
  }
  get focused() {
    return isActiveElement(this._input);
  }
  get value() {
    var _this$_input2;
    return (_this$_input2 = this._input) === null || _this$_input2 === void 0 ? void 0 : _this$_input2.value;
  }
  get id() {
    return this.props.id || this._defaultId;
  }
  renderInput() {
    var _this$props$styles;
    const _this$props3 = this.props,
      type = _this$props3.type,
      size = _this$props3.size,
      htmlSize = _this$props3.htmlSize,
      display = _this$props3.display,
      textAlign = _this$props3.textAlign,
      placeholder = _this$props3.placeholder,
      value = _this$props3.value,
      defaultValue = _this$props3.defaultValue,
      isRequired = _this$props3.isRequired,
      onFocus = _this$props3.onFocus,
      rest = _objectWithoutProperties(_this$props3, _excluded);
    const props = passthroughProps(rest);
    const interaction = this.interaction;
    let descriptionIds = '';
    if (props['aria-describedby']) {
      descriptionIds = `${props['aria-describedby']}`;
    }
    return _jsx("input", {
      ...props,
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.textInput,
      defaultValue: defaultValue,
      value: value,
      placeholder: placeholder,
      ref: this.handleInputRef,
      type: type,
      id: this.id,
      required: isRequired,
      "aria-invalid": this.invalid ? 'true' : void 0,
      disabled: interaction === 'disabled',
      readOnly: interaction === 'readonly',
      "aria-describedby": descriptionIds !== '' ? descriptionIds : void 0,
      size: htmlSize,
      onChange: this.handleChange,
      onBlur: this.handleBlur
    });
  }
  getElementHasWidth(element) {
    if (!element) {
      return void 0;
    }
    const computedStyle = getComputedStyle(element);
    const width = computedStyle.width,
      paddingInlineStart = computedStyle.paddingInlineStart,
      paddingInlineEnd = computedStyle.paddingInlineEnd;
    if (width === 'auto' || width === '') {
      // This is a workaround for an edge-case, when the TextInput's parent
      // is hidden on load, so the element is not visible either.
      // In this case the computed width is going to be either 'auto' or '',
      // so we assume it has width so that the padding won't be removed.
      return true;
    }
    const elementWidth = parseFloat(width) - parseFloat(paddingInlineStart) - parseFloat(paddingInlineEnd);
    return elementWidth > 0;
  }
  render() {
    const _this$props4 = this.props,
      width = _this$props4.width,
      display = _this$props4.display,
      renderLabel = _this$props4.renderLabel,
      renderBeforeInput = _this$props4.renderBeforeInput,
      renderAfterInput = _this$props4.renderAfterInput,
      messages = _this$props4.messages,
      inputContainerRef = _this$props4.inputContainerRef,
      isRequired = _this$props4.isRequired,
      styles = _this$props4.styles;
    const beforeElement = renderBeforeInput ? callRenderProp(renderBeforeInput) : null;
    const afterElement = renderAfterInput ? callRenderProp(renderAfterInput) : null;
    const renderBeforeOrAfter = !!beforeElement || !!afterElement || renderBeforeInput !== void 0 || renderAfterInput !== void 0;
    const rawLabel = callRenderProp(renderLabel);
    const label = hasVisibleChildren(rawLabel) ? _jsxs(Fragment, {
      children: [rawLabel, isRequired && _jsxs("span", {
        css: this.invalid ? styles === null || styles === void 0 ? void 0 : styles.requiredInvalid : {},
        "aria-hidden": true,
        children: [' ', "*"]
      })]
    }) : rawLabel;
    return _jsx(FormField, {
      id: this.id,
      label: label,
      messagesId: this._messagesId,
      messages: messages,
      inline: display === 'inline-block',
      width: width,
      inputContainerRef: inputContainerRef,
      layout: this.props.layout,
      elementRef: this.handleRef,
      margin: this.props.margin,
      children: _jsx("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.facade,
        children: renderBeforeOrAfter ? _jsxs("span", {
          css: styles === null || styles === void 0 ? void 0 : styles.layout,
          children: [beforeElement, _jsxs("span", {
            css: styles === null || styles === void 0 ? void 0 : styles.inputLayout,
            children: [this.renderInput(), afterElement && _jsx("span", {
              css: styles === null || styles === void 0 ? void 0 : styles.afterElement,
              ref: e => {
                this._afterElement = e;
              },
              children: afterElement
            })]
          })]
        }) : (/* If no prepended or appended content, don't render Flex layout */
        this.renderInput())
      })
    });
  }
}, _TextInput.displayName = "TextInput", _TextInput.componentId = 'TextInput', _TextInput.allowedProps = allowedProps, _TextInput.propTypes = propTypes, _TextInput.defaultProps = {
  type: 'text',
  // Leave interaction default undefined so that `disabled` and `readOnly` can also be supplied
  interaction: void 0,
  isRequired: false,
  display: 'block',
  shouldNotWrap: false,
  size: 'medium',
  textAlign: 'start',
  messages: []
}, _TextInput)) || _class) || _class) || _class);
export default TextInput;
export { TextInput };